import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button, Card, Pill, Spinner } from "@/components/ui-kit";
import { Search, GraduationCap, UserPlus, X } from "lucide-react";

const inputCls = "w-full rounded-[var(--ns-radius)] border-2 border-[var(--ns-border)] bg-[var(--ns-surface)] px-4 py-2.5 focus:outline-none focus:border-[var(--ns-primary)]";

export default function AdminTeachers() {
  const [teachers, setTeachers] = useState(null);
  const [tq, setTq] = useState("");
  const [selected, setSelected] = useState(null);

  const loadTeachers = async () => {
    const { data } = await api.get("/admin/teachers");
    setTeachers(data.teachers);
    return data.teachers;
  };
  useEffect(() => { loadTeachers(); }, []);

  if (!teachers) return <div><h2 className="font-head text-2xl font-bold mb-4">Teacher assignments</h2><Spinner label="Loading teachers" /></div>;

  const filtered = teachers.filter((t) =>
    !tq || (t.name || "").toLowerCase().includes(tq.toLowerCase()) || (t.email || "").toLowerCase().includes(tq.toLowerCase()));

  return (
    <div data-testid="admin-teachers">
      <h2 className="font-head text-2xl font-bold mb-1">Teacher assignments</h2>
      <p className="text-sm text-[var(--ns-muted)] mb-4">Assign students to teachers. Teachers only ever see students assigned here.</p>

      {teachers.length === 0 ? (
        <Card className="p-6 text-center text-[var(--ns-muted)]" data-testid="no-teachers">
          No teacher accounts yet. Create one in User management (set a user's role to <span className="font-semibold">teacher</span>), then assign students here.
        </Card>
      ) : (
        <div className="grid md:grid-cols-[300px_1fr] gap-5">
          {/* Teacher list */}
          <Card className="p-3">
            <div className="relative mb-3">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ns-subtle)]" />
              <input data-testid="teacher-search-input" value={tq} onChange={(e) => setTq(e.target.value)} placeholder="Find a teacher" className={inputCls + " pl-9 py-2"} />
            </div>
            <div className="grid gap-1 max-h-[360px] overflow-y-auto">
              {filtered.map((t) => (
                <button key={t.id} data-testid={`teacher-item-${t.id}`} onClick={() => setSelected(t)}
                  className={`text-left rounded-[var(--ns-radius)] px-3 py-2.5 transition-colors ${selected?.id === t.id ? "bg-[var(--ns-primary)] text-white" : "hover:bg-[var(--ns-bg)]"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold truncate">{t.name || t.email}</span>
                    <span className={`text-xs shrink-0 ${selected?.id === t.id ? "text-white/80" : "text-[var(--ns-subtle)]"}`}>{t.assigned_count}</span>
                  </div>
                  <div className={`text-xs truncate ${selected?.id === t.id ? "text-white/80" : "text-[var(--ns-muted)]"}`}>{t.email}</div>
                </button>
              ))}
              {filtered.length === 0 && <div className="text-sm text-[var(--ns-muted)] p-3">No teachers match.</div>}
            </div>
          </Card>

          {/* Assignment panel */}
          <Card className="p-5">
            {!selected ? (
              <div className="grid place-items-center h-full min-h-[200px] text-center text-[var(--ns-muted)]">
                <div><GraduationCap size={28} className="mx-auto mb-2 text-[var(--ns-subtle)]" />Select a teacher to manage their students.</div>
              </div>
            ) : (
              <TeacherPanel teacher={selected} onCountChange={async () => { const list = await loadTeachers(); setSelected(list.find((x) => x.id === selected.id) || null); }} />
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

function TeacherPanel({ teacher, onCountChange }) {
  const [assigned, setAssigned] = useState(null);
  const [sq, setSq] = useState("");
  const [all, setAll] = useState([]);
  const [busy, setBusy] = useState("");

  const loadAssigned = async () => {
    const { data } = await api.get(`/admin/teachers/${teacher.id}/students`);
    setAssigned(data.students);
  };
  const loadAll = async (query = "") => {
    const { data } = await api.get(`/admin/students${query ? `?q=${encodeURIComponent(query)}` : ""}`);
    setAll(data.students);
  };
  useEffect(() => { setAssigned(null); loadAssigned(); loadAll(); }, [teacher.id]);

  const assignedIds = new Set((assigned || []).map((s) => s.id));

  const assign = async (s) => {
    setBusy(s.id);
    try { await api.post(`/admin/teachers/${teacher.id}/students`, { student_id: s.id }); await loadAssigned(); onCountChange(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(""); }
  };
  const unassign = async (s) => {
    setBusy(s.id);
    try { await api.delete(`/admin/teachers/${teacher.id}/students/${s.id}`); await loadAssigned(); onCountChange(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setBusy(""); }
  };

  if (!assigned) return <Spinner label="Loading students" />;
  const candidates = all.filter((s) => !assignedIds.has(s.id));

  return (
    <div data-testid="teacher-panel">
      <div className="flex items-center gap-2 mb-4">
        <GraduationCap size={18} className="text-[var(--ns-primary)]" />
        <span className="font-head font-bold text-[var(--ns-text)]">{teacher.name || teacher.email}</span>
        <Pill tone="muted">{assigned.length} assigned</Pill>
      </div>

      <div className="mb-5">
        <div className="text-sm font-semibold text-[var(--ns-text)] mb-2">Assigned students</div>
        {assigned.length === 0 ? (
          <p className="text-sm text-[var(--ns-muted)]" data-testid="teacher-no-students">No students assigned yet.</p>
        ) : (
          <div className="grid gap-2">
            {assigned.map((s) => (
              <div key={s.id} data-testid={`assigned-student-${s.id}`} className="flex items-center justify-between rounded-[var(--ns-radius)] border border-[var(--ns-border)] px-3 py-2">
                <span className="text-sm"><span className="font-semibold">{s.name}</span> <span className="text-[var(--ns-subtle)]">· Grade {gradeText(s.grade)}</span></span>
                <button data-testid={`unassign-btn-${s.id}`} onClick={() => unassign(s)} disabled={busy === s.id}
                  className="inline-flex items-center gap-1 text-sm font-semibold text-[#B23A28] hover:opacity-80">
                  <X size={15} /> Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="border-t border-[var(--ns-border)] pt-4">
        <div className="text-sm font-semibold text-[var(--ns-text)] mb-2">Add students</div>
        <form onSubmit={(e) => { e.preventDefault(); loadAll(sq); }} className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ns-subtle)]" />
            <input data-testid="assign-search-input" value={sq} onChange={(e) => setSq(e.target.value)} placeholder="Search students by name" className={inputCls + " pl-9"} />
          </div>
          <Button type="submit" variant="outline" data-testid="assign-search-btn">Search</Button>
        </form>
        <div className="grid gap-2 max-h-[260px] overflow-y-auto">
          {candidates.length === 0 && <p className="text-sm text-[var(--ns-muted)]">No unassigned students match.</p>}
          {candidates.map((s) => (
            <div key={s.id} data-testid={`candidate-student-${s.id}`} className="flex items-center justify-between rounded-[var(--ns-radius)] border border-[var(--ns-border)] px-3 py-2">
              <span className="text-sm"><span className="font-semibold">{s.name}</span> <span className="text-[var(--ns-subtle)]">· Grade {gradeText(s.grade)}</span></span>
              <Button variant="subtle" data-testid={`assign-student-btn-${s.id}`} onClick={() => assign(s)} disabled={busy === s.id}>
                <UserPlus size={15} /> Assign
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function gradeText(g) {
  return g === 0 ? "K" : g;
}
